/**
 * Umami analytics shim (self-hosted at telemetry.zestian.co).
 *
 * Single provider — call sites only use `init`, `track`, `trackPageview`. Tests
 * mock `window.umami` directly; no env var indirection.
 *
 * CSP hosts this module needs (kept here so future swaps stay mechanical —
 * update `static/_headers` together with this file):
 *   - script-src:  https://telemetry.zestian.co
 *   - connect-src: https://telemetry.zestian.co (ingest at /collect)
 *
 * SPA pageviews: script tag has `data-auto-track="false"` so Gatsby route
 * changes do not auto-fire — `gatsby-browser.tsx#onRouteUpdate` calls
 * `trackPageview` with a SCRUBBED url (e.g. `/guias/:slug`) via Umami's
 * function-override form of `track`.
 */

export const WEBSITE_ID = "687b7b10-c276-4bb3-8e81-1dae3bb8f60e";
export const SCRIPT_SRC = "https://telemetry.zestian.co/script.js";

const SCRIPT_ID = "umami-loader";
const IS_DEV =
  typeof process !== "undefined" && process.env.NODE_ENV === "development";

export type EventMap = {
  cta_click: {
    destination: "stats" | "discord" | "store" | "copy_ip" | "play";
    location: "hero" | "nav" | "footer" | "modal";
  };
  server_ip_copy: { source: "hero" | "footer" | "modal" };
  mode_card_click: { mode_slug: string; position: number };
  rules_section_open: { section_slug: string };
  partner_click: { partner_slug: string };
  guide_open: { guide_slug: string };
  social_click: {
    platform: "discord" | "instagram" | "tiktok" | "youtube" | "twitter";
    location: "nav" | "footer";
  };
  language_switch: { from: string; to: string };
  nav_click: { destination: string };
};

type UmamiTrackProps = {
  hostname: string;
  language: string;
  referrer: string;
  screen: string;
  title: string;
  url: string;
  website: string;
};

type UmamiGlobal = {
  track: {
    (eventName: string, eventData?: Record<string, unknown>): void;
    (overrides: (props: UmamiTrackProps) => Partial<UmamiTrackProps>): void;
  };
};

declare global {
  interface Window {
    umami?: UmamiGlobal;
  }
}

const pending: Array<() => void> = [];

function umami(): UmamiGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return window.umami;
}

function safe(fn: () => void): void {
  try {
    fn();
  } catch (e) {
    if (IS_DEV) console.warn("[analytics:umami]", e);
  }
}

function flush(): void {
  let fn: (() => void) | undefined;
  while (umami() && pending.length > 0) {
    fn = pending.shift();
    if (fn) safe(fn);
  }
}

function dispatch(fn: () => void): void {
  if (umami()) safe(fn);
  else pending.push(fn);
}

export function init(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (document.getElementById(SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.defer = true;
  script.src = SCRIPT_SRC;
  script.dataset.websiteId = WEBSITE_ID;
  script.dataset.autoTrack = "false";
  document.head.appendChild(script);
  script.addEventListener("load", flush, { once: true });
}

export function track<K extends keyof EventMap>(name: K, data: EventMap[K]): void {
  dispatch(() => {
    umami()!.track(name, data as Record<string, unknown>);
  });
}

export function trackPageview(url: string, title?: string): void {
  dispatch(() => {
    umami()!.track((props) => ({
      ...props,
      url,
      ...(title !== undefined ? { title } : {}),
    }));
  });
}
